import {Injectable, inject} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {forkJoin, merge, Observable, of, Subject, zip} from 'rxjs';
import {map, mergeMap} from 'rxjs/operators';
import {i18nKeyMap} from '../../_utils/lang';
import {exists} from '../../_utils/operators';

const LANG_CODE_KEY = 'api.lang.code';

/**
 * A service to set titles from the i18n resource bundles.  Changing the language afterwards will automatically update the title.
 */
@Injectable({
  providedIn: 'root'
})
export class I18nService {
  private readonly title = inject(Title);
  private readonly translateService = inject(TranslateService);


  private _currentLangShortCode = 'EN';
  private readonly titleKey$ = new Subject<string>();

  constructor() {
    this.translateService.stream('api.lang.short-code').subscribe(v => this._currentLangShortCode = v);
    this.titleKey$.pipe(mergeMap(key => this.getTranslation(key))).subscribe(translation => this.title.setTitle(translation));

    //Ensure that whatever the current language is set to is reflected in the html tag
    this.translateService.onLangChange.pipe(
      map((value: LangChangeEvent) => value.lang),
      exists()
    ).subscribe(currentLang => {
      document.querySelector('html').setAttribute('lang', currentLang);
      //Also store the language to help with initializing the app in the future.
      localStorage.setItem('i18n-set-language', currentLang);
    });
  }

  setTitle(i18nKey: string): void {
    this.titleKey$.next(i18nKey);
  }

  toggleEngFr() {
    if(this.currentLang === 'en-CA') {
      console.info('Switching to french');
      this.translateService.use('fr-CA');
    } else {
      console.info('Switching to english');
      this.translateService.use('en-CA');
    }
  }

  /**
   * Useful for generated translation keys where there's concern the key won't exist.  In such case, return a provided default.
   */
  getTruthyTranslation(key: string, defaultIfNull: string, data: Record<string, unknown> = {}) {
    if(key) {
      return this.getTranslation(key, data);
    } else {
      return of(defaultIfNull);
    }
  }

  getTranslation(key: string, data: Record<string, unknown> = {}): Observable<string> {
    return this.translateService.stream(key, data);
  }

  getTranslations(...keys: string[]): Observable<string[]> {
    return zip(...keys.map(value => this.getTranslation(value)));
  }

  /**
   * Take an object with i18n keys as the values, and turn that into a map with the translations.  The map will be re-emitted when the current language
   * is changed.
   */
  getTranslationMap(toTranslate: {[key: string]: string}): Observable<Map<string, string>> {
    return this.repeatOnLangChange(() =>
      forkJoin(
        //Convert the object of i18n keys to an object of observables that forkJoin can work with
        Object.fromEntries(
          Object.entries(toTranslate).map(([k, v]) => [k, this.translateService.get(v)] as [string, Observable<string>])
        ) as {[k: string]: Observable<string>}
      )
    ).pipe(map((v: {[key: string]: string}) => new Map(Object.entries(v))));
  }

  /**
   * Call the provided observable immediately and also whenever the current language changes
   */
  repeatOnLangChange<T>(obs: () => Observable<T>): Observable<T> {
    //Emit at least once via of(1), and also emit when the language changes via onLangChange.  Convert that into the provided observable.  Merged into a single observable.
    return merge(of(1), this.translateService.onLangChange).pipe(mergeMap(() => obs()));
  }

  formatCurrency(value: number | string, defaultAmount?: number): Observable<string> {
    const _value: number = !isNaN(Number(value)) ? Number(value) : defaultAmount;
    if(isNaN(_value)) {
      return of(null);
    }
    return zip(this.getTranslation(LANG_CODE_KEY), this.getTranslation('api.currency')).pipe(map(translations => {
      const langCode = translations[0];
      const currency = translations[1];
      return new Intl.NumberFormat(langCode, {style: 'currency', currency: currency}).format(_value);
    }));
  }

  formatPercent(value: number | string, maximumFractionDigits = 1): Observable<string> {
    if(value === null) {
      return of(null);
    }
    return this.getTranslation(LANG_CODE_KEY).pipe(
      map(langCode => new Intl.NumberFormat(langCode, {style: 'percent', maximumFractionDigits: maximumFractionDigits}).format(Number(value)))
    );
  }

  formatNumber(value: number | string): Observable<string> {
    if(!value) {
      return of(null);
    }
    return this.getTranslation(LANG_CODE_KEY).pipe(map(langCode => new Intl.NumberFormat(langCode).format(Number(value))));
  }

  get provinceNames(): Observable<Map<string, string>> {
    return this.getTranslationMap(i18nKeyMap('content.keyed.provinces.', ['AB', 'BC', 'MB', 'NB', 'NL', 'NT', 'NS', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT']));
  }

  get cardTypes(): Observable<Map<string, string>> {
    return this.getTranslationMap({
      '':       'content.select-one',
      master:   'content.card.master',
      visa:     'content.card.visa',
      cibc:     'content.card.cibc',
      hbc:      'content.card.hbc',
      samsclub: 'content.card.samsclub',
      walmart:  'content.card.walmart',
      linen:    'content.card.linen',
      jcpenney: 'content.card.jcpenney',
      regency:  'content.card.regency',
      bassett:  'content.card.bassett',
      petro:    'content.card.petro',
      imperial: 'content.card.imperial',
      chase:    'content.card.chase',
      holt:     'content.card.holt',
      laz:      'content.card.laz'
    });
  }

  get timeZones(): Observable<Map<string, string>> {
    return this.getTranslationMap({
      10: 'content.keyed.timezones.10',
      20: 'content.keyed.timezones.20',
      30: 'content.keyed.timezones.30',
      40: 'content.keyed.timezones.40',
      50: 'content.keyed.timezones.50',
      60: 'content.keyed.timezones.60'
    });
  }

  get currentLang(): string {
    return this.translateService.currentLang;
  }

  get currentLangShortCode(): string {
    return this._currentLangShortCode;
  }

}
